import { BaseService, ServiceResponse, PaginatedResponse } from './base.service';
import type { Database } from '../../integrations/supabase/types';

export type Club = Database['public']['Tables']['clubs']['Row'];
export type ClubWithCourses = Club & {
  courses?: Array<{
    id: string;
    name: string;
    hole_count: number | null;
    has_gps: boolean | null;
  }>;
};

export interface ClubSearchParams {
  search?: string;
  country?: string;
  limit?: number;
  offset?: number;
}

export interface ClubStats {
  totalClubs: number;
  clubsWithCourses: number;
  countriesCount: number;
  avgCoursesPerClub: number;
}

export interface ClubAmenities {
  driving_range?: boolean;
  putting_green?: boolean;
  chipping_area?: boolean;
  bunker_practice?: boolean;
  trackman?: boolean;
  pro_shop?: boolean;
  restaurant?: boolean;
  bar?: boolean;
  locker_rooms?: boolean;
  showers?: boolean;
  caddie_service?: boolean;
  cart_rental?: boolean;
  club_rental?: boolean;
  lesson_facilities?: boolean;
  swimming_pool?: boolean;
  tennis_courts?: boolean;
  fitness_center?: boolean;
}

export interface ClubProfileUpdate {
  name?: string;
  description?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
  website_url?: string;
  club_type?: 'private' | 'public' | 'semi_private';
  operating_hours?: string;
  established_year?: number;
  member_capacity?: number;
  logo_url?: string;
  cover_image_url?: string;
  // Social media
  facebook_url?: string;
  instagram_url?: string;
  twitter_url?: string;
  youtube_url?: string;
  linkedin_url?: string;
  // Amenities
  amenities?: ClubAmenities;
}

class ClubsService extends BaseService {
  
  /**
   * Get all clubs with basic information
   */
  async getAllClubs(): Promise<ServiceResponse<Club[]>> {
    return this.withErrorHandling(async () => {
      const { data, error } = await this.supabase
        .from('clubs')
        .select('*')
        .order('name');

      if (error) throw error;
      return data || [];
    });
  }

  /**
   * Search clubs with filters and pagination
   */
  async searchClubs(params: ClubSearchParams = {}): Promise<PaginatedResponse<Club>> {
    return this.withPaginatedErrorHandling(async () => {
      let query = this.supabase
        .from('clubs')
        .select('*', { count: 'exact' });

      // Apply search filter
      if (params.search) {
        query = query.or(`name.ilike.%${params.search}%,city.ilike.%${params.search}%,address.ilike.%${params.search}%`);
      }

      // Apply country filter
      if (params.country) {
        query = query.eq('country', params.country);
      }

      // Apply pagination
      const limit = params.limit || 20;
      const offset = params.offset || 0;
      query = query.range(offset, offset + limit - 1);

      // Order by name
      query = query.order('name');

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        data: data || [],
        pagination: {
          total: count || 0,
          page: Math.floor(offset / limit),
          limit
        }
      };
    });
  }

  /**
   * Get clubs by country (focused on Belgium)
   */
  async getClubsByCountry(country: string = 'Belgium'): Promise<ServiceResponse<Club[]>> {
    return this.withErrorHandling(async () => {
      const { data, error } = await this.supabase
        .from('clubs')
        .select('*')
        .eq('country', country)
        .order('name');

      if (error) throw error;
      return data || [];
    });
  }

  /**
   * Get detailed club information with courses
   */
  async getClubDetails(clubId: string): Promise<ServiceResponse<ClubWithCourses>> {
    return this.withErrorHandling(async () => {
      // Get club details
      const { data: club, error: clubError } = await this.supabase
        .from('clubs')
        .select('*')
        .eq('id', clubId)
        .single();

      if (clubError) throw clubError;

      // Get courses for this club
      const { data: courses, error: coursesError } = await this.supabase
        .from('courses')
        .select('id, name, hole_count, has_gps')
        .eq('club_id', clubId)
        .order('name');

      if (coursesError) throw coursesError;

      return {
        ...club,
        courses: courses || []
      };
    });
  }

  /**
   * Get clubs with their course counts
   */
  async getClubsWithCourseCounts(country?: string): Promise<ServiceResponse<ClubWithCourses[]>> {
    return this.withErrorHandling(async () => {
      let query = this.supabase
        .from('clubs')
        .select(`
          *,
          courses (
            id,
            name,
            hole_count,
            has_gps
          )
        `);

      if (country) {
        query = query.eq('country', country);
      }

      query = query.order('name');

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    });
  }

  /**
   * Get clubs statistics
   */
  async getClubStats(country?: string): Promise<ServiceResponse<ClubStats>> {
    return this.withErrorHandling(async () => {
      let clubQuery = this.supabase
        .from('clubs')
        .select('id, course_count, country', { count: 'exact' });

      if (country) {
        clubQuery = clubQuery.eq('country', country);
      }

      const { data: clubs, error: clubError, count: totalClubs } = await clubQuery;

      if (clubError) throw clubError;

      // Calculate stats
      const clubsWithCourses = clubs?.filter(club => (club.course_count || 0) > 0).length || 0;
      const countries = new Set(clubs?.map(club => club.country).filter(Boolean));
      const avgCoursesPerClub = clubs?.length 
        ? clubs.reduce((sum, club) => sum + (club.course_count || 0), 0) / clubs.length 
        : 0;

      return {
        totalClubs: totalClubs || 0,
        clubsWithCourses,
        countriesCount: countries.size,
        avgCoursesPerClub: Math.round(avgCoursesPerClub * 10) / 10
      };
    });
  }

  /**
   * Get featured/popular clubs (clubs with most courses)
   */
  async getFeaturedClubs(limit: number = 6): Promise<ServiceResponse<Club[]>> {
    return this.withErrorHandling(async () => {
      const { data, error } = await this.supabase
        .from('clubs')
        .select('*')
        .not('course_count', 'is', null)
        .gte('course_count', 1)
        .order('course_count', { ascending: false })
        .order('name')
        .limit(limit);

      if (error) throw error;
      return data || [];
    });
  }

  /**
   * Get nearby clubs (placeholder for future GPS implementation)
   */
  async getNearbyClubs(latitude?: number, longitude?: number, radiusKm: number = 50): Promise<ServiceResponse<Club[]>> {
    return this.withErrorHandling(async () => {
      // For now, return all clubs in Belgium since we don't have GPS coordinates yet
      // In the future, this would use PostGIS functions for distance calculations
      const { data, error } = await this.supabase
        .from('clubs')
        .select('*')
        .eq('country', 'Belgium')
        .order('name')
        .limit(10);

      if (error) throw error;
      return data || [];
    });
  }

  /**
   * Get user's favorite clubs (temporarily disabled)
   */
  async getUserFavoriteClubs(userId: string): Promise<ServiceResponse<string[]>> {
    // Temporarily disabled - return empty array
    return { data: [], error: null, success: true };
  }

  /**
   * Add club to user's favorites (temporarily disabled)
   */
  async addToFavorites(userId: string, clubId: string): Promise<ServiceResponse<boolean>> {
    // Temporarily disabled - return success without doing anything
    return { data: true, error: null, success: true };
  }

  /**
   * Remove club from user's favorites (temporarily disabled)
   */
  async removeFromFavorites(userId: string, clubId: string): Promise<ServiceResponse<boolean>> {
    // Temporarily disabled - return success without doing anything
    return { data: true, error: null, success: true };
  }

  /**
   * Get clubs marked as favorites by user (temporarily disabled)
   */
  async getFavoriteClubsDetails(userId: string): Promise<ServiceResponse<Club[]>> {
    // Temporarily disabled - return empty array
    return { data: [], error: null, success: true };
  }

  /**
   * Get club for admin (with authorization check)
   */
  async getClubForAdmin(clubId: string, userId: string): Promise<ServiceResponse<Club>> {
    return this.withErrorHandling(async () => {
      // First verify user is admin of this club
      const { data: membership, error: membershipError } = await this.supabase
        .from('user_club_memberships')
        .select('role, status')
        .eq('user_id', userId)
        .eq('club_id', clubId)
        .eq('status', 'active')
        .in('role', ['admin', 'owner'])
        .single();

      if (membershipError || !membership) {
        throw new Error('Unauthorized: You must be an admin or owner of this club');
      }

      // Get club data
      const { data: club, error: clubError } = await this.supabase
        .from('clubs')
        .select('*')
        .eq('id', clubId)
        .single();

      if (clubError) throw clubError;
      if (!club) throw new Error('Club not found');

      return club;
    });
  }

  /**
   * Update club profile (admin only)
   */
  async updateClubProfile(clubId: string, updates: ClubProfileUpdate, userId: string): Promise<ServiceResponse<Club>> {
    return this.withErrorHandling(async () => {
      // First verify user is admin of this club
      const { data: membership, error: membershipError } = await this.supabase
        .from('user_club_memberships')
        .select('role, status')
        .eq('user_id', userId)
        .eq('club_id', clubId)
        .eq('status', 'active')
        .in('role', ['admin', 'owner'])
        .single();

      if (membershipError || !membership) {
        throw new Error('Unauthorized: You must be an admin or owner of this club');
      }

      // Filter out undefined values
      const cleanUpdates = Object.fromEntries(
        Object.entries(updates).filter(([_, value]) => value !== undefined)
      );

      // Update club with new data
      const { data: updatedClub, error: updateError } = await this.supabase
        .from('clubs')
        .update(cleanUpdates)
        .eq('id', clubId)
        .select()
        .single();

      if (updateError) throw updateError;
      if (!updatedClub) throw new Error('Failed to update club');

      return updatedClub;
    });
  }

  /**
   * Get clubs where user is admin
   */
  async getUserAdminClubs(userId: string): Promise<ServiceResponse<Club[]>> {
    return this.withErrorHandling(async () => {
      const { data: clubs, error } = await this.supabase
        .from('clubs')
        .select(`
          *,
          user_club_memberships!inner (
            role,
            status
          )
        `)
        .eq('user_club_memberships.user_id', userId)
        .eq('user_club_memberships.status', 'active')
        .in('user_club_memberships.role', ['admin', 'owner'])
        .order('name');

      if (error) throw error;
      return clubs || [];
    });
  }
}

export const clubsService = new ClubsService();
export default clubsService;